import DefaultImage from "@src/app/assets/default-thumbnail.jpg";
import cx from "classnames";
import { type ImgHTMLAttributes, useState } from "react";

import { getFormattedCaaSImageUrl } from "./helper";

export type Prettify<T> = {
  [K in keyof T]: T[K];
};

export type OptimisedImageProps = Prettify<
  ImgHTMLAttributes<HTMLImageElement> & {
    rootClassName?: string;
    src?: string;
    filters?: Record<string, CaaSImageFilters>;
    pictureRootClassName?: string;
    suffix?: React.ReactNode;
  }
>;

export type CaaSImageFilters = {
  breakpoint?: string; // breakpoint
  f?: "jpeg" | "png" | "webp" | "avif" | "gif" | "svg"; // format
  w?: number; // width 10 to 1280
  h?: number; // height 10 to 1280
  q?: number; // quality 20 - 100
  dpr?: number; // dpr 1 - 3
};

const smFilter: CaaSImageFilters = { w: 360, q: 80, dpr: 3, f: "webp" };
const mdFilter: CaaSImageFilters = { w: 480, q: 80, dpr: 3, f: "webp" };
const lgFilter: CaaSImageFilters = { w: 720, q: 80, dpr: 3, f: "webp" };
const xlFilter: CaaSImageFilters = { w: 960, q: 80, dpr: 2, f: "webp" };
const xxlFilter: CaaSImageFilters = { w: 1290, q: 80, dpr: 3, f: "webp" };

// CaaS Image API
// - https://sph.atlassian.net/wiki/spaces/PAPP/pages/1514865469/CaaS+how-tos#Image-optimisation-API
// - https://github.com/SPHTech-Applications/caas-platform-img-optimisation-lambda/blob/main/docs/quick-start.md#how-to-use-the-image-optimisation-api
export default function OptimisedImage({
  pictureRootClassName,
  rootClassName,
  src,
  filters = {
    xxl: { f: "avif", w: 600, q: 80, dpr: 3 },
    lg: { f: "avif", w: 800, q: 80, dpr: 3 },
    md: { f: "avif", w: 600, q: 100, dpr: 3 },
    sm: { f: "avif", w: 500, q: 100, dpr: 3 },
  },
  suffix,
  ...rest
}: OptimisedImageProps): React.ReactElement {
  const [imageError, setImageError] = useState<boolean>(false);

  const computedFilters = {
    sm: smFilter,
    md: mdFilter,
    lg: lgFilter,
    xl: xlFilter,
    xxl: xxlFilter,
    ...filters,
  };

  return (
    <div className="relative overflow-hidden">
      <picture className={cx(pictureRootClassName)}>
        {src && !imageError ? (
          <>
            <source
              media="(min-width: 1024px)"
              srcSet={getFormattedCaaSImageUrl(src, computedFilters.xxl)}
              type="image/avif"
            />
            <source
              media="(min-width: 992px)"
              srcSet={getFormattedCaaSImageUrl(src, computedFilters.xl)}
              type="image/avif"
            />

            <source
              media="(min-width: 576px)"
              srcSet={getFormattedCaaSImageUrl(src, computedFilters.lg)}
              type="image/avif"
            />

            <source
              media="(min-width: 320px)"
              srcSet={getFormattedCaaSImageUrl(src, computedFilters.md)}
              type="image/avif"
            />

            <source
              srcSet={getFormattedCaaSImageUrl(src, computedFilters.sm)}
              type="image/avif"
            />

            <img
              className={cx(
                "w-full max-w-full transition-transform duration-300",
                rootClassName,
              )}
              src={getFormattedCaaSImageUrl(src, computedFilters.xl)}
              alt={rest.alt || ""}
              onError={() => {
                setImageError(true);
              }}
              {...rest}
            />
          </>
        ) : null}

        {imageError || !src ? (
          <img
            className={cx(
              "hidden! w-full max-w-full transition-transform duration-300 lg:block!",
              rootClassName,
            )}
            src={DefaultImage}
            alt={rest.alt || ""}
          />
        ) : null}

        {imageError || !src ? (
          <img
            className={cx(
              "block! w-full max-w-full transition-transform duration-300 lg:hidden!",
              rootClassName,
            )}
            src={DefaultImage}
            alt={rest.alt || ""}
          />
        ) : null}
      </picture>
      {suffix}
    </div>
  );
}
